// ============================================================================
// InactiveClients — the "gone quiet / needs follow-up" surface (Wave 6).
// ----------------------------------------------------------------------------
// Surfaces clients who have stopped engaging with the portal: their
// `last_active_at` is older than the trainer's inactivity window (or they've
// never signed in). Backend-driven (GET /api/clients/inactive) — distinct from
// the dashboard roster's session-recency follow-up lens, which is derived from
// the calendar. Each row shows how long since the client was last active
// (localized via Intl.RelativeTimeFormat → zh-Hant) and links to the client's
// profile + a one-tap Message affordance that deep-links to their Messages
// thread so the trainer can reach out.
//
// Two surfaces share this component:
//   • <InactiveClientsCard/> — full Card on the trainer dashboard.
//   • <InactiveClientsSection/> — the same list, used inline on the roster page.
// Both pull the one shared `useInactiveClients()` query (kept fresh by the
// `client` live-sync invalidation, so a client coming back drops off live).
//
// Pulse, mobile-first, reduced-motion aware. Every string is i18n (en + zh).
// ============================================================================
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Avatar, Badge, Card, CardHeader, EmptyState } from "../components";
import {
  IconBellAlert,
  IconChevronRight,
  IconMessage,
} from "../components/icons";
import { useInactiveClients } from "../hooks/clients";
import { formatRelativeTime } from "../lib/date";
import type { InactiveClient } from "../api";
import { cn } from "../lib/cn";

/** One quiet-client row: avatar, name, recency, profile link + Message CTA. */
function InactiveRow({ client }: { client: InactiveClient }) {
  const { t } = useTranslation("inactivity");

  const recency = client.last_active_at
    ? t("lastActive", { when: formatRelativeTime(client.last_active_at) })
    : t("neverActive");

  return (
    <li
      className={cn(
        "flex items-center gap-3 rounded-md bg-amber-soft/40 p-2.5 ring-1 ring-amber/20",
        "transition-all duration-fast hover:-translate-y-0.5 hover:shadow-sm",
      )}
    >
      <Avatar name={client.name} size="md" status="alert" />

      <Link
        to={`/app/clients/${client.id}`}
        aria-label={t("openProfile", { name: client.name })}
        className="min-w-0 flex-1"
      >
        <div className="flex items-center gap-2">
          <span className="truncate font-display text-sm font-bold text-body">
            {client.name}
          </span>
          <Badge tone="warning" className="shrink-0">
            {t("badge")}
          </Badge>
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-body-soft">
          <span className="truncate font-bold text-amber-600">{recency}</span>
          {client.inactivity_emailed_at && (
            <>
              <span className="text-body-faint">·</span>
              <span
                className="truncate text-body-muted"
                aria-label={t("emailedAria", {
                  when: formatRelativeTime(client.inactivity_emailed_at),
                })}
              >
                {t("emailed", {
                  when: formatRelativeTime(client.inactivity_emailed_at),
                })}
              </span>
            </>
          )}
        </div>
      </Link>

      {/* One-tap reach-out: deep-links straight to this client's Messages tab. */}
      <Link
        to={`/app/clients/${client.id}?tab=messages`}
        aria-label={t("messageAria", { name: client.name })}
        className={cn(
          "inline-flex shrink-0 items-center gap-1.5 rounded-pill bg-grad-coral px-3 py-1.5",
          "text-xs font-extrabold text-white shadow-volt transition-all duration-fast",
          "hover:-translate-y-0.5 hover:shadow-volt-lg focus-visible:outline-none",
        )}
      >
        <IconMessage width={14} height={14} />
        <span className="hidden sm:inline">{t("message")}</span>
      </Link>
    </li>
  );
}

interface InactiveListProps {
  clients: InactiveClient[];
  /** Cap the rendered rows (the rest are summarized by a "see all" footer). */
  max?: number;
  /** Where the "see all" footer links (the full roster, follow-up filtered). */
  seeAllTo?: string;
}

/** The shared body: a capped list of quiet clients + an optional overflow link. */
function InactiveList({ clients, max, seeAllTo }: InactiveListProps) {
  const { t } = useTranslation("inactivity");
  const shown = typeof max === "number" ? clients.slice(0, max) : clients;
  const overflow = clients.length - shown.length;

  return (
    <>
      <ul className="space-y-1.5">
        {shown.map((c) => (
          <InactiveRow key={c.id} client={c} />
        ))}
      </ul>
      {overflow > 0 && seeAllTo && (
        <Link
          to={seeAllTo}
          className={cn(
            "mt-3 inline-flex items-center gap-1 text-sm font-bold text-coral",
            "transition-colors hover:text-volt-600",
          )}
        >
          {t("seeAll", { count: clients.length })}
          <IconChevronRight width={16} height={16} />
        </Link>
      )}
    </>
  );
}

/**
 * Full dashboard Card. Renders nothing while loading or when everyone's active
 * (the dashboard shouldn't show an empty follow-up card competing for space) —
 * the card only appears when there's a real nudge to make. Pass `max` to cap
 * rows on the dashboard and link the overflow to the roster's follow-up filter.
 */
export function InactiveClientsCard({ max = 4 }: { max?: number }) {
  const { t } = useTranslation("inactivity");
  const { data, isLoading, isError } = useInactiveClients();

  // Stay invisible until we have a positive signal — no skeleton flash, no empty
  // card. The roster page owns the "all caught up" empty state instead.
  if (isLoading || isError) return null;
  const clients = data?.clients ?? [];
  const days = data?.days ?? 0;
  if (clients.length === 0) return null;

  return (
    <Card>
      <CardHeader
        title={
          <span className="inline-flex items-center gap-2">
            <span className="grid h-7 w-7 place-items-center rounded-pill bg-amber-soft text-amber-600">
              <IconBellAlert width={16} height={16} />
            </span>
            {t("title")}
          </span>
        }
        subtitle={t("subtitle", { count: clients.length })}
        action={
          <Badge tone="warning" solid>
            {clients.length}
          </Badge>
        }
      />
      <p className="-mt-2 mb-3 text-xs font-medium text-body-muted">
        {t("windowNote", { count: days })}
      </p>
      <InactiveList
        clients={clients}
        max={max}
        seeAllTo="/app/clients?filter=followUp"
      />
    </Card>
  );
}

/**
 * Inline section for the roster page. Unlike the dashboard card, this owns its
 * own loading + "all caught up" + error states because the roster page wants a
 * stable slot. `compact` trims the header to a thin banner-like strip.
 */
export function InactiveClientsSection() {
  const { t } = useTranslation("inactivity");
  const { data, isLoading, isError } = useInactiveClients();

  if (isLoading) return null; // The roster's own loader covers the first paint.

  if (isError) {
    return (
      <Card>
        <p className="text-sm text-body-soft">{t("loadError")}</p>
      </Card>
    );
  }

  const clients = data?.clients ?? [];
  const days = data?.days ?? 0;

  if (clients.length === 0) {
    return (
      <Card>
        <EmptyState
          icon={
            <span className="grid h-14 w-14 place-items-center rounded-full bg-lime-soft text-lime-600">
              <IconBellAlert width={26} height={26} />
            </span>
          }
          title={t("allCaughtUp")}
          description={t("allCaughtUpBlurb")}
        />
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader
        title={
          <span className="inline-flex items-center gap-2">
            <span className="grid h-7 w-7 place-items-center rounded-pill bg-amber-soft text-amber-600">
              <IconBellAlert width={16} height={16} />
            </span>
            {t("title")}
          </span>
        }
        subtitle={t("subtitle", { count: clients.length })}
        action={
          <Badge tone="warning" solid>
            {clients.length}
          </Badge>
        }
      />
      <p className="-mt-2 mb-3 text-xs font-medium text-body-muted">
        {t("windowNote", { count: days })}
      </p>
      <InactiveList clients={clients} />
    </Card>
  );
}
